import { Button, Menu, MenuItem, IconButton, Tooltip, Divider } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, type ReactNode } from "react";
import { useAuthSimple } from "../../hooks/useAuth";
import { empresaService, grupoService } from "../../services/entitiesServices";
import { Empresa } from "../../types/api";
import { Grupo } from "../../types/entities";
import { ConfirmDialog } from '../ui/ConfirmDialog';

// Icons as outline SVGs to match card style
const Icons: Record<string, ReactNode> = {
  home: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1h-5v-6H9v6H4a1 1 0 01-1-1V9.5z" />
    </svg>
  ),
  dashboard: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 13h8V3H3v10zM3 21h8v-6H3v6zM13 21h8V11h-8v10zM13 3v6h8V3h-8z" />
    </svg>
  ),
  clients: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-4-4h-1M9 20H4v-2a4 4 0 014-4h1m0-4a4 4 0 11-8 0 4 4 0 018 0zm8 0a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  policies: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h10M7 16h6M5 3h14a1 1 0 011 1v16l-6-3-6 3V4a1 1 0 011-1z" />
    </svg>
  ),
  calendar: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M3 11h18M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  warning: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4m0 4h.01" />
    </svg>
  ),
  money: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  upload: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5 5 5M12 5v12" />
    </svg>
  ),
  settings: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2h-1a2 2 0 01-2-2v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2v-1a2 2 0 012-2h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82L3.21 4.21A2 2 0 015 1.38l.06.06a1.65 1.65 0 001.82.33H7a1.65 1.65 0 001-1.51V1a2 2 0 012-2h1a2 2 0 012 2v.09c.3.97.96 1.75 1.9 2.1.95.35 1.92.1 2.5-.5l.06-.06A2 2 0 0120.62 3l-.06.06a1.65 1.65 0 00-.33 1.82V7c0 .55.22 1.05.58 1.41z" />
    </svg>
  ),
  logout: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h6a2 2 0 012 2v1" />
    </svg>
  ),
  menu: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  ),
  user: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A9 9 0 1118.879 6.196 9 9 0 015.12 17.804zM15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  company: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21V8a1 1 0 011-1h16a1 1 0 011 1v13M9 21V10h6v11" />
    </svg>
  ),
  group: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20v-2a4 4 0 00-4-4H7a4 4 0 00-4 4v2M12 12a4 4 0 100-8 4 4 0 000 8z" />
    </svg>
  ),
  card: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <rect x="2" y="5" width="20" height="14" rx="2" ry="2" strokeWidth={2} />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2 10h20M7 16h6" />
    </svg>
  ),
  target: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" strokeWidth={2} />
      <circle cx="12" cy="12" r="6" strokeWidth={2} />
      <circle cx="12" cy="12" r="2" strokeWidth={2} />
    </svg>
  ),
  clipboard: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 2h6a2 2 0 012 2v1H7V4a2 2 0 012-2z" />
      <rect x="3" y="7" width="18" height="14" rx="2" ry="2" strokeWidth={2} />
    </svg>
  ),
  shield: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2l7 4v6c0 5-3.58 9.74-7 11-3.42-1.26-7-6-7-11V6l7-4z" />
    </svg>
  ),
  package: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7l9-4 9 4M21 7v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v14" />
    </svg>
  ),
  chart: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3v18h18" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 13v6M12 9v10M17 5v14" />
    </svg>
  ),
};

export const Navbar = () => {
  const { logout, user } = useAuthSimple();
  const location = useLocation();
  const [empresa, setEmpresa] = useState<Empresa | null>(null);
  const [grupo, setGrupo] = useState<Grupo | null>(null);
  const [configMenuAnchor, setConfigMenuAnchor] = useState<null | HTMLElement>(null);
  const [operationalMenuAnchor, setOperationalMenuAnchor] = useState<null | HTMLElement>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadUserData = async () => {
      // If backend provided empresaNome on login, use it and avoid extra API call.
      if (user?.empresaId) {
        if (user.empresaNome) {
          setEmpresa({ id: user.empresaId, nome: user.empresaNome } as Empresa);
        } else {
          // only attempt API call if we have an auth token; otherwise it will return 401/require token
          const token = localStorage.getItem('authToken');
          if (token) {
            try {
              const empresaResponse = await empresaService.getById(user.empresaId);
              setEmpresa(empresaResponse.data);
            } catch (error) {
                console.error('Erro ao carregar empresa:', error);
                // Fallback: sometimes the BY_ID endpoint denies access but the LIST endpoint
                // returns the companies the user can see. Try fetching the list and find by id.
                try {
                  const listRes: any = await empresaService.list();
                  // listRes might be an array or a paginated object { data: [...] }
                  // or a domain-shaped response like { empresas: [...] }
                  let arr: any[] = [];
                  if (Array.isArray(listRes)) arr = listRes as any[];
                  else if (listRes && Array.isArray(listRes.data)) arr = listRes.data;
                  else if (listRes && Array.isArray(listRes.items)) arr = listRes.items;
                  else if (listRes && Array.isArray(listRes.empresas)) arr = listRes.empresas;

                  const found = arr.find((e: any) => e.id === user.empresaId);
                  if (found) setEmpresa(found as Empresa);
                } catch (listErr) {
                  console.error('Fallback ao listar empresas também falhou:', listErr);
                }
              // keep empresa null; fallback to user.empresaNome if later provided
            }
          } else {
            // no token available yet, rely on user info only
            setEmpresa(null);
          }
        }
      }

      if (user?.grupoId) {
        if (user.grupoNome) {
          setGrupo({ id: user.grupoId, nome: user.grupoNome } as Grupo);
        } else {
          const token = localStorage.getItem('authToken');
          if (token) {
            try {
              const grupoResponse = await grupoService.getById(user.grupoId);
              setGrupo(grupoResponse.data);
            } catch (error) {
              console.error('Erro ao carregar grupo:', error);
              // Fallback: try to get group from list if BY_ID is restricted
              try {
                const listRes: any = await grupoService.list();
                let arr: any[] = [];
                if (Array.isArray(listRes)) arr = listRes as any[];
                else if (listRes && Array.isArray(listRes.data)) arr = listRes.data;
                else if (listRes && Array.isArray(listRes.items)) arr = listRes.items;
                else if (listRes && Array.isArray(listRes.grupos)) arr = listRes.grupos;

                const found = arr.find((g: any) => g.id === user.grupoId);
                if (found) setGrupo(found as Grupo);
              } catch (listErr) {
                console.error('Fallback ao listar grupos também falhou:', listErr);
              }
            }
          } else {
            setGrupo(null);
          }
        }
      }
    };

    loadUserData();
    // Listen for auth changes so the navbar reloads company/group immediately
    const onAuthChange = (e: Event) => {
      try {
        // @ts-ignore
        const detail = (e as CustomEvent).detail;
        console.log('[Navbar] auth:change event received', detail);
      } catch (err) {
        console.log('[Navbar] auth:change event received (no detail)');
      }
      loadUserData();
    };

    window.addEventListener('auth:change', onAuthChange as EventListener);

    return () => {
      window.removeEventListener('auth:change', onAuthChange as EventListener);
    };
  }, [user?.empresaId, user?.grupoId]);

  const handleLogout = () => {
    setLogoutConfirmOpen(true);
  };

  const handleConfirmLogout = async () => {
    setLogoutConfirmOpen(false);
    await logout();
  };

  const handleConfigMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setConfigMenuAnchor(event.currentTarget);
  };

  const handleConfigMenuClose = () => {
    setConfigMenuAnchor(null);
  };

  const handleOperationalMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOperationalMenuAnchor(event.currentTarget);
  };

  const handleOperationalMenuClose = () => {
    setOperationalMenuAnchor(null);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const getUserInfo = () => {
    if (!user) return { text: 'Carregando...', icon: Icons.user };
    
    let userInfo = user.nome || user.email;
    let icon = Icons.user;
    
    if (user.type === 'empresa') {
      icon = Icons.company;
      if (empresa) {
        userInfo += ` - ${empresa.nome}`;
      } else if (user.empresaNome) {
        userInfo += ` - ${user.empresaNome}`;
      } else if (user.accessScope?.empresaId) {
        userInfo += ` - Empresa (${user.accessScope.empresaId})`;
      } else if (user.permissions?.empresaIds && user.permissions.empresaIds.length > 0) {
        userInfo += ` - Empresa (${user.permissions.empresaIds[0]})`;
      }
    } else if (user.type === 'master') {
      userInfo += ' (Master)';
      icon = "🔑";
    } else if (user.type === 'grupo') {
      icon = Icons.group;
      if (grupo) {
        userInfo += ` - ${grupo.nome}`;
      } else if (user.grupoNome) {
        userInfo += ` - ${user.grupoNome}`;
      } else if (user.permissions?.grupoIds && user.permissions.grupoIds.length > 0) {
        userInfo += ` - Grupo (${user.permissions.grupoIds[0]})`;
      }
    }
    
    return { text: userInfo, icon };
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  const NavButton = ({ to, icon, children, isActive }: { to: string; icon: ReactNode; children: React.ReactNode; isActive?: boolean }) => (
    <Tooltip title={children}>
      <Button 
        component={Link} 
        to={to} 
        variant={isActive ? "contained" : "text"}
        size="small" 
        sx={{ 
          color: isActive ? '#5d54a4' : '#FFFFFF',
          backgroundColor: isActive ? '#FFFFFF' : 'transparent',
          fontSize: '0.75rem',
          minWidth: 'auto',
          px: 2,
          '&:hover': {
            backgroundColor: isActive ? '#f5f5f5' : 'rgba(255,255,255,0.1)'
          }
        }}
      >
        <span style={{ marginRight: '4px' }}>{icon}</span>
        {children}
      </Button>
    </Tooltip>
  );

  const userInfo = getUserInfo();



  return (
    <nav className="flex justify-between items-center p-3 shadow-lg" style={{ 
      backgroundColor: '#5d54a4',
      background: 'linear-gradient(135deg, #5d54a4 0%, #7A61B2 100%)'
    }}>
      {/* Logo + User Info */}
      <div className="flex items-center space-x-3">
        <div className="flex items-center">
          <img src="/AixaCorretor.svg" alt="AIXA" className="h-8 mr-3" />
        </div>
        <div>
          <div className="text-xs font-semibold text-white">{userInfo.text}</div>
        </div>
      </div>

      {/* Navigation Links */}
      {isMobile ? (
        /* Mobile Menu */
        <div>
            <IconButton onClick={handleMobileMenuOpen} sx={{ color: '#FFFFFF' }}>
            <span>{Icons.menu}</span>
          </IconButton>
          <Menu
            anchorEl={mobileMenuAnchor}
            open={Boolean(mobileMenuAnchor)}
            onClose={handleMobileMenuClose}
          >
            <MenuItem component={Link} to="/home" onClick={handleMobileMenuClose}>
              {Icons.home} Home
            </MenuItem>
            <MenuItem component={Link} to="/dashboard" onClick={handleMobileMenuClose}>
              {Icons.dashboard} Dashboard
            </MenuItem>
            <MenuItem component={Link} to="/clientes" onClick={handleMobileMenuClose}>
              {Icons.clients} Clientes
            </MenuItem>
            <MenuItem component={Link} to="/apolice" onClick={handleMobileMenuClose}>
              {Icons.policies} Apólices
            </MenuItem>
            <MenuItem component={Link} to="/cotacoes" onClick={handleMobileMenuClose}>
              {Icons.clipboard} Cotações
            </MenuItem>
            <Divider />
            <MenuItem component={Link} to="/agendamentos" onClick={handleMobileMenuClose}>
              {Icons.calendar} Agendamentos
            </MenuItem>
            <MenuItem component={Link} to="/digisac" onClick={handleMobileMenuClose}>
              📲 Digisac
            </MenuItem>
            <MenuItem component={Link} to="/sinistros" onClick={handleMobileMenuClose}>
              {Icons.warning} Sinistros
            </MenuItem>
            <MenuItem component={Link} to="/repasses" onClick={handleMobileMenuClose}>
              {Icons.money} Repasses
            </MenuItem>
            <MenuItem component={Link} to="/repasses-por-documento" onClick={handleMobileMenuClose}>
              {Icons.money} Repasse por Documento
            </MenuItem>
            <Divider />
            <MenuItem component={Link} to="/parcelas" onClick={handleMobileMenuClose}>
              {Icons.card} Parcelas
            </MenuItem>
            <MenuItem component={Link} to="/metas-comissao" onClick={handleMobileMenuClose}>
              {Icons.target} Metas de Comissão
            </MenuItem>
            <Divider />
            <MenuItem component={Link} to="/seguradoras" onClick={handleMobileMenuClose}>
              <span style={{ marginRight: '8px' }}>{Icons.shield}</span>
              Seguradoras
            </MenuItem>
            <MenuItem component={Link} to="/produtos" onClick={handleMobileMenuClose}>
              <span style={{ marginRight: '8px' }}>{Icons.package}</span>
              Produtos
            </MenuItem>
            <MenuItem component={Link} to="/situacoes" onClick={handleMobileMenuClose}>
              <span style={{ marginRight: '8px' }}>{Icons.chart}</span>
              Situações
            </MenuItem>
            <MenuItem component={Link} to="/importacoes" onClick={handleMobileMenuClose}>
              <span style={{ marginRight: '8px' }}>{Icons.upload}</span>
              Importações
            </MenuItem>
            <MenuItem component={Link} to="/configuracoes" onClick={handleMobileMenuClose}>
              {Icons.settings} Configurações
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { handleMobileMenuClose(); handleLogout(); }}>
              {Icons.logout} Sair
            </MenuItem>
          </Menu>
        </div>
      ) : (
        /* Desktop Menu */
        <div className="flex items-center space-x-1">
          {/* Core Navigation */}
          <div className="flex items-center space-x-1 mr-4">
            <NavButton to="/home" icon={Icons.home} isActive={isActiveRoute('/home')}>
              Home
            </NavButton>
            <NavButton to="/dashboard" icon={Icons.dashboard} isActive={isActiveRoute('/dashboard')}>
              Dashboard
            </NavButton>
            <NavButton to="/clientes" icon={Icons.clients} isActive={isActiveRoute('/clientes')}>
              Clientes
            </NavButton>
            <NavButton to="/apolice" icon={Icons.policies} isActive={isActiveRoute('/apolice')}>
              Apólices
            </NavButton>
            <NavButton to="/cotacoes" icon={Icons.clipboard} isActive={isActiveRoute('/cotacoes')}>
              Cotações
            </NavButton>
          </div>

          {/* Operational Menu */}
          <div className="flex items-center space-x-1 mr-4">
            <Button 
              variant="text" 
              size="small" 
              onClick={handleOperationalMenuOpen}
              sx={{ 
                color: '#FFFFFF', 
                fontSize: '0.75rem',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
              }}
            >
              <span style={{ marginRight: '4px' }}>{Icons.clipboard}</span>
              Operacional ▼
            </Button>
            <Menu
              anchorEl={operationalMenuAnchor}
              open={Boolean(operationalMenuAnchor)}
              onClose={handleOperationalMenuClose}
            >
              <MenuItem component={Link} to="/agendamentos" onClick={handleOperationalMenuClose}>
                <span style={{ marginRight: '8px' }}>{Icons.calendar}</span>
                Agendamentos
              </MenuItem>
              <MenuItem component={Link} to="/digisac" onClick={handleOperationalMenuClose}>
                <span style={{ marginRight: '8px' }}>📲</span>
                Digisac
              </MenuItem>
              <MenuItem component={Link} to="/sinistros" onClick={handleOperationalMenuClose}>
                <span style={{ marginRight: '8px' }}>{Icons.warning}</span>
                Sinistros
              </MenuItem>
              <MenuItem component={Link} to="/repasses" onClick={handleOperationalMenuClose}>
                <span style={{ marginRight: '8px' }}>{Icons.money}</span>
                Repasses
              </MenuItem>
              <MenuItem component={Link} to="/repasses-por-documento" onClick={handleOperationalMenuClose}>
                <span style={{ marginRight: '8px' }}>{Icons.money}</span>
                Repasse por Documento
              </MenuItem>
              <Divider />
              <MenuItem component={Link} to="/parcelas" onClick={handleOperationalMenuClose}>
                <span style={{ marginRight: '8px' }}>{Icons.card}</span>
                Parcelas
              </MenuItem>
              <MenuItem component={Link} to="/metas-comissao" onClick={handleOperationalMenuClose}>
                <span style={{ marginRight: '8px' }}>{Icons.target}</span>
                Metas de Comissão
              </MenuItem>
            </Menu>
          </div>

          {/* Tools */}
          {/* Importações moved into Configurações menu */}

          {/* Configuration Menu */}
          <div className="flex items-center space-x-1 mr-4">
            <Button 
              variant="text" 
              size="small" 
              onClick={handleConfigMenuOpen}
              sx={{ 
                color: '#FFFFFF', 
                fontSize: '0.75rem',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
              }}
            >
              <span style={{ marginRight: '4px' }}>{Icons.settings}</span>
              Configurações ▼
            </Button>
            <Menu
              anchorEl={configMenuAnchor}
              open={Boolean(configMenuAnchor)}
              onClose={handleConfigMenuClose}
            >
              <MenuItem component={Link} to="/seguradoras" onClick={handleConfigMenuClose}>
                <span style={{ marginRight: '8px' }}>{Icons.shield}</span>
                Seguradoras
              </MenuItem>
              <MenuItem component={Link} to="/produtos" onClick={handleConfigMenuClose}>
                <span style={{ marginRight: '8px' }}>{Icons.package}</span>
                Produtos
              </MenuItem>
              <MenuItem component={Link} to="/situacoes" onClick={handleConfigMenuClose}>
                <span style={{ marginRight: '8px' }}>{Icons.chart}</span>
                Situações
              </MenuItem>
              <MenuItem component={Link} to="/importacoes" onClick={handleConfigMenuClose}>
                <span style={{ marginRight: '8px' }}>{Icons.upload}</span>
                Importações
              </MenuItem>
              <Divider />
              <MenuItem component={Link} to="/configuracoes" onClick={handleConfigMenuClose}>
                <span style={{ marginRight: '8px' }}>{Icons.settings}</span>
                Outras Configurações
              </MenuItem>
            </Menu>
          </div>

          {/* Logout */}
          <Button 
            variant="outlined" 
            size="small" 
            onClick={handleLogout} 
            sx={{ 
              color: '#FFFFFF', 
              borderColor: '#FFFFFF', 
              fontSize: '0.75rem',
              '&:hover': { 
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderColor: '#FFFFFF'
              }
            }}
          >
            <span style={{ marginRight: '4px' }}>{Icons.logout}</span>
            Sair
          </Button>
        </div>
      )}

      <ConfirmDialog
        isOpen={logoutConfirmOpen}
        title="Sair do Sistema"
        message="Tem certeza que deseja sair?"
        confirmText="Sair"
        cancelText="Cancelar"
        confirmButtonColor="red"
        onConfirm={handleConfirmLogout}
        onCancel={() => setLogoutConfirmOpen(false)}
      />
    </nav>
  );
};

export default Navbar;